import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { AgentAvatar } from "@/components/AgentAvatar";
import { Ion } from "@/components/Ion";
import { DESKTOP_BREAKPOINT, useTheme, useWindowWidth } from "@/lib/theme";

// Pages where the original mounts AIFloatingButton.
const AI_FAB_EXACT = ["/dashboard", "/training", "/training-course", "/lso-course", "/operations", "/manual-viewer", "/support"];

export const AIFloatingButton = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  return (
    <button data-testid="ai-floating-button" onClick={() => navigate("/ai-assistant")} aria-label="Open ALR Cleaning Agent"
      className="fixed bottom-6 right-6 z-[1000] flex h-14 w-14 items-center justify-center rounded-full transition-transform duration-150 active:scale-95"
      style={{ boxShadow: `0 4px 14px ${theme.accent}66` }}>
      <AgentAvatar kind="ai" size={56} ring={theme.accent} />
    </button>
  );
};

export const GlobalAIFab = () => {
  const { user } = useAuth();
  const { pathname } = useLocation();
  if (!user) return null;
  if (!AI_FAB_EXACT.includes(pathname) && !pathname.startsWith("/section/")) return null;
  return <AIFloatingButton />;
};

export const HelpFab = () => {
  const { user, loading } = useAuth();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  if (loading || !user) return null;
  if (["machine_owner", "rental_customer", "prospect"].includes((user.role || "").toLowerCase())) return null;
  if (pathname === "/" || pathname === "/index" || pathname.startsWith("/guide")) return null;
  return (
    <button data-testid="help-fab" aria-label="Open ALR Guide" onClick={() => navigate("/guide?mode=help")}
      className="fixed bottom-24 right-6 z-[1000] flex h-12 w-12 items-center justify-center rounded-full border bg-[#0A0A0A] transition-transform duration-150 active:scale-95"
      style={{ borderColor: theme.accent, boxShadow: `0 4px 14px ${theme.accent}66` }}>
      <span className="-mt-px text-[22px] font-black leading-6" style={{ color: theme.accent }}>?</span>
    </button>
  );
};

const HIDE_ON_EXACT = ["/", "/dashboard", "/lso-course", "/prospect", "/prospect-portal", "/insurance", "/guide"];
const LIFT_ON_EXACT = ["/ai-assistant", "/customer-community", "/support"];
const LIFT_ON_PREFIX = ["/sales-coach", "/community", "/feedback"];
const FRANCHISE_JOURNEY = ["/prospect-portal", "/prospect-preview", "/prospect-complete-profile", "/franchisee-assessment", "/financial-qualification"];
const under = (path, p) => path === p || path.startsWith(`${p}/`);

export const HomeFloatingButton = () => {
  const { user } = useAuth();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const width = useWindowWidth();
  if (!user || width >= DESKTOP_BREAKPOINT) return null;
  if (HIDE_ON_EXACT.includes(pathname) || under(pathname, "/admin")) return null;
  const home = user.role === "rental_customer" ? "/lso-course"
    : user.role === "prospect" ? (FRANCHISE_JOURNEY.some((p) => under(pathname, p)) ? "/prospect-portal" : "/prospect")
    : "/dashboard";
  const lift = LIFT_ON_EXACT.includes(pathname) || LIFT_ON_PREFIX.some((p) => under(pathname, p));
  return (
    <button data-testid="home-floating-button" aria-label="Back to home" onClick={() => navigate(home, { replace: true })}
      className="fixed left-6 z-[1000] flex h-[52px] w-[52px] items-center justify-center rounded-full border"
      style={{ bottom: lift ? 110 : 24, background: theme.bg, borderColor: theme.accent, boxShadow: `0 4px 14px ${theme.accent}55` }}>
      <span className="flex h-10 w-10 items-center justify-center rounded-full" style={{ background: theme.accent }}><Ion name="home" size={22} color="#000" /></span>
    </button>
  );
};
